import { useEffect, useRef, useState, type ReactNode } from 'react';
import dayjs from 'dayjs';
import Slider from 'react-slick';
import { useAllInvoicesStore, useAllStatementsStore, useCardsProductsStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { CardUtils, cn, FormatUtils, RoutePaths } from '@shared/utils';
import type { GetAllCardsResponse, ImagesType } from '@shared/api';
import cardImg from '@assets/images/royal-digital-card.png';
import ArrowIcon from '@assets/images/arrow.svg';
import styles from './cardSlider.module.css';

interface ArrowProps {
  className?: string;
  inactive?: boolean;
  onClick?: () => void;
}
const NextArrow = (props: ArrowProps) => (
  <div className={cn('flex h-full items-center', props.className)} onClick={props.onClick}>
    <ArrowIcon className={cn('relative left-[10%]', props.inactive && 'opacity-50')} />
  </div>
);
const PrevArrow = (props: ArrowProps) => (
  <div className={cn('flex h-full items-center', props.className)} onClick={props.onClick}>
    <ArrowIcon className={cn('rotate-180', props.inactive && 'opacity-50')} />
  </div>
);

const settings = {
  focusOnSelect: true,
  dots: false,
  infinite: false,
  speed: 500,
  variableWidth: true,
  slidesToShow: 1,
  slidesToScroll: 1,
};

interface CardSliderProps {
  className?: string;
  cardClassName?: string;
  imageClassName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cards: any[];
  showCardId: number | null;
  goToSlideNumber?: number;
  cardImage?: keyof ImagesType;
  toggleCards?: boolean;
  showNameOverCard?: boolean;
  isNeedSeparator?: boolean;
  dontRotate?: boolean;
  renderButtons?: () => ReactNode;
  onSelectCard: (card: GetAllCardsResponse, index: number) => void;
}

export const CardSlider = (props: CardSliderProps) => {
  const { cardImage = 'front' } = props;

  const { navigate } = useRouteHook();

  const cardsProductsStore = useCardsProductsStore();
  const allInvoicesStore = useAllInvoicesStore();
  const allStatementsStore = useAllStatementsStore();

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    if (props.cards.length && (!props.cards[0].images || !props.cards[0].images[cardImage])) cardsProductsStore.get();
  }, [props.cards]);

  useEffect(() => {
    if (sliderRef.current && props.goToSlideNumber !== undefined) {
      const timerId = setTimeout(() => sliderRef.current?.slickGoTo(props.goToSlideNumber!), 250);
      return () => clearTimeout(timerId);
    }
    return undefined;
  }, [props.goToSlideNumber, sliderRef.current]);

  return (
    <div className={cn(props.className)}>
      {!!props.cards.length && (
        <Slider
          ref={sliderRef}
          className='cardSlider'
          {...{
            ...settings,
            nextArrow: <NextArrow inactive={currentIndex >= props.cards.length / 7} />,
            prevArrow: <PrevArrow inactive={currentIndex === 0} />,
          }}
          beforeChange={(_, nextSlide) => setCurrentIndex(nextSlide)}
        >
          {props.cards.map((card, idx) => (
            <>
              <div
                key={idx}
                className={cn(
                  'ml-6 flex w-auto shrink-0 basis-full cursor-pointer flex-col',
                  'focus:outline-none',
                  'max-md:ml-0',
                  props.toggleCards && props.showCardId !== idx
                    ? props.showNameOverCard
                      ? ['after:absolute after:top-3.75 after:right-0 after:bottom-0 after:left-0 after:bg-[#ffffff80]', 'after:max-md:hidden']
                      : ['relative', 'after:absolute after:top-0 after:right-0 after:bottom-0 after:left-0 after:rounded-2xl after:bg-[#ffffff80]']
                    : null,
                  props.cardClassName,
                )}
                onClick={() => {
                  if (props.showCardId === idx) {
                    // props.onSelectCard(null, null);
                  } else {
                    props.onSelectCard(card, idx);
                    sliderRef.current?.slickGoTo(idx);
                  }

                  allStatementsStore.getAllStatementsCard(card.id);
                  allInvoicesStore.getAllInvoicesCard(card.id);
                }}
              >
                {card.paymentDue && (
                  <div className='relative'>
                    <div
                      className={cn(
                        'absolute right-0 z-1 ml-auto h-7.5 w-34 cursor-pointer overflow-hidden rounded-tr-[10px] pl-7.5 text-[18px] leading-7.5 text-white',
                        'hover:underline',
                        'after:absolute after:top-5 after:-right-[685%] after:z-[-1] after:h-[1100%] after:w-[1100%] after:origin-[54%_0] after:rotate-65 after:bg-[#b70000]',
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(RoutePaths.cards.onNavigate(), { state: { scrollToCardId: card.id } });
                      }}
                    >
                      invoice due
                    </div>
                  </div>
                )}
                {props.showNameOverCard && (
                  <div className={cn('mb-1.5 flex w-full justify-center text-xs font-medium text-[#272727]', 'max-md:hidden')}>{card.name}</div>
                )}
                {card.currency && card.cardNumber ? (
                  props.showCardId === idx && !props.dontRotate ? (
                    <div
                      className={cn(
                        'h-63 w-100 cursor-pointer overflow-hidden rounded-2xl bg-contain',
                        'max-md:aspect-320/205 max-md:h-auto max-md:w-auto max-md:bg-no-repeat',
                      )}
                    >
                      <div className='flex h-20.5 w-99.75 bg-[#4a4a4a]'>
                        <div className='mt-3 mr-3 flex grow'>
                          <div className='flex h-5.5 w-5.5 items-center justify-center rounded-full bg-[#b70000] text-white'>!</div>
                          <div className='pl-1 text-[11px] tracking-[0.66px] text-[#cfcfd4]'>
                            <div>Outstanding Balance:</div>
                            <div>
                              {card.currency}
                              {card.statementBalance}
                            </div>
                            {allInvoicesStore.data && allInvoicesStore.data.length && allInvoicesStore.data[0].date ? (
                              <div>Invoice Date: {dayjs(allInvoicesStore.data[0].date).format('DD/MM/YYYY')}</div>
                            ) : null}
                            {card.settelmentDate ? <div>Settelment Date: {dayjs(card.settelmentDate).format('DD/MM/YYYY')}</div> : null}
                          </div>
                        </div>
                        <div>
                          {allInvoicesStore.data && allInvoicesStore.data.length && allInvoicesStore.data[0].paymentDue && (
                            <div
                              className={cn(
                                'relative z-1 ml-auto h-7.5 w-26.5 cursor-pointer overflow-hidden rounded-tr-[10px] pl-7.5 text-lg text-white',
                                'hover:underline',
                                'after:top-5 after:right-[-683%] after:z-[-1] after:h-[1100%] after:w-[1100%] after:origin-[54%_0] after:rotate-65 after:overflow-hidden after:bg-[#b70000]',
                              )}
                              onClick={() => navigate(RoutePaths.cards.onNavigate(), { state: card })}
                            >
                              invoice due
                            </div>
                          )}
                          <div className='ml-5 w-99.75 text-white'>
                            <div>{card.currency}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'h-63 w-100 cursor-pointer overflow-hidden rounded-2xl bg-contain pt-7',
                        'max-md:aspect-320/205 max-md:h-auto max-md:w-auto max-md:bg-no-repeat',
                        props.isNeedSeparator && props.showCardId !== idx && 'shadow-none',
                      )}
                      style={{ backgroundImage: `url(${CardUtils.renderBackgroundImg(card, cardsProductsStore.data)})` }}
                    >
                      <div className={cn('mt-6.5 ml-5 pt-7.5 text-[29px] whitespace-nowrap text-white', 'max-md:text-[13px]')}>
                        <div style={{ color: CardUtils.getColor(card, cardsProductsStore.data) }}>{card.currency}</div>
                      </div>
                      <p
                        className={cn('mt-3.75 ml-6.5 text-xl tracking-[6px] whitespace-nowrap text-white', 'max-md:text-[13px]')}
                        style={{ color: CardUtils.getColor(card, cardsProductsStore.data) }}
                      >
                        {FormatUtils.formatCardNumber(card.cardNumber)}
                      </p>
                      <p
                        className={cn('mt-6.5 ml-6.5 text-xl tracking-[6px] whitespace-nowrap text-white', 'max-md:text-[13px]')}
                        style={{ color: CardUtils.getColor(card, cardsProductsStore.data) }}
                      >
                        {card.cardName}
                      </p>
                    </div>
                  )
                ) : (
                  <img
                    className={cn(props.isNeedSeparator && props.showCardId !== idx && 'shadow-none!', props.imageClassName)}
                    src={card.images && card.images[cardImage] ? `${process.env.API_URL}/${card.images[cardImage]}` : cardImg}
                    alt='text'
                  />
                )}
                {props.isNeedSeparator ? (
                  props.showCardId !== idx ? (
                    <div
                      className={cn(
                        'absolute -right-px bottom-0 -left-px z-2 h-13.5 bg-[#fafafa]',
                        'before:absolute before:h-6.25 before:w-6.25 max-md:hidden',
                        styles['card-arrow-separator'],
                      )}
                    />
                  ) : (
                    <div className={cn('z-2 mx-auto my-2 h-px w-4.5 bg-black', 'max-md:hidden')} />
                  )
                ) : null}
              </div>
              {props.showCardId === idx && <div className={cn('mt-12.5 ml-6.25', 'max-md:hidden')}>{props.renderButtons?.()}</div>}
            </>
          ))}
        </Slider>
      )}
    </div>
  );
};
